
import React, { useState } from 'react';
import { AccountFinal } from '@/api/entities';
import { Loader2, CheckCircle, AlertTriangle, Upload, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function TempAccountFitUpload() {
    const [status, setStatus] = useState('idle'); // idle, running, done
    const [logs, setLogs] = useState([]);
    const [summary, setSummary] = useState({ updated: 0, notFound: 0, errors: 0 });
    const [csvData, setCsvData] = useState('');
    const [diagnosticData, setDiagnosticData] = useState(null);
    const [isLoadingDiagnostic, setIsLoadingDiagnostic] = useState(false);

    const addLog = (message, type = 'info') => {
        setLogs(prev => [...prev, { message, type }]);
    };

    const handleCsvChange = (event) => {
        setCsvData(event.target.value);
    };

    const parseCsv = (csvText) => {
        const lines = csvText.trim().split('\n');
        if (lines.length === 0) {
            return { records: [], delimiter: ',' }; // Default to comma if no lines
        }
        const headerLine = lines[0];

        // Auto-detect delimiter (comma or tab)
        let delimiter = ',';
        let splitFunction;

        if (headerLine.includes('\t')) {
            delimiter = '\t';
            // For tab-separated, a simple split should be sufficient as tabs are less likely to be within quotes
            splitFunction = (line) => line.split('\t');
        } else {
            // For comma-separated, use regex to handle commas inside quoted fields
            splitFunction = (line) => line.split(/,(?=(?:(?:[^"]*"){2})*[^"]*$)/);
        }

        const headers = splitFunction(headerLine).map(h => h.trim().replace(/"/g, ''));
        
        const records = lines.slice(1).map(line => {
            const values = splitFunction(line).map(v => v.trim().replace(/"/g, ''));
            const record = {};
            headers.forEach((header, index) => {
                record[header] = values[index];
            });
            return record;
        });

        return { records, delimiter };
    };

    const runDiagnostic = async () => {
        setIsLoadingDiagnostic(true);
        try {
            const allAccounts = await AccountFinal.list();
            
            const diagnostic = {
                totalAccounts: allAccounts.length,
                accountsWithIds: allAccounts.filter(acc => acc.account_id).length,
                accountsWithoutIds: allAccounts.filter(acc => !acc.account_id).length,
                sampleAccountIds: allAccounts.filter(acc => acc.account_id).slice(0, 10).map(acc => ({ 
                    account_id: acc.account_id, 
                    account_name: acc.account_name 
                })),
                sampleAccountsWithoutIds: allAccounts.filter(acc => !acc.account_id).slice(0, 10).map(acc => ({ 
                    account_name: acc.account_name,
                    id: acc.id
                })),
                allAccountIds: allAccounts.filter(acc => acc.account_id).map(acc => acc.account_id).sort()
            };
            
            setDiagnosticData(diagnostic);
        } catch (error) {
            console.error('Diagnostic error:', error);
        }
        setIsLoadingDiagnostic(false);
    };

    const runUpdate = async () => {
        if (!csvData.trim()) {
            addLog('Please paste CSV data first.', 'error');
            return;
        }

        setStatus('running');
        setLogs([]);
        setSummary({ updated: 0, notFound: 0, errors: 0 });

        let tempSummary = { updated: 0, notFound: 0, errors: 0 };

        try {
            addLog('Parsing CSV data...');
            const { records: csvRecords, delimiter } = parseCsv(csvData);
            addLog(`Parsed ${csvRecords.length} records from CSV using a "${delimiter === '\t' ? 'Tab' : 'Comma'}" delimiter.`);

            addLog('Fetching all accounts from database...');
            const allAccounts = await AccountFinal.list();
            addLog(`Found ${allAccounts.length} accounts in database.`);

            // Create comprehensive maps for matching
            const accountIdMap = new Map();
            const accountNameMap = new Map();
            
            allAccounts.forEach(acc => {
                // Map by account_id if it exists
                if (acc.account_id) {
                    accountIdMap.set(acc.account_id, acc);
                    accountIdMap.set(acc.account_id.toLowerCase().trim(), acc);
                }
                
                // Map by account_name
                if (acc.account_name) {
                    accountNameMap.set(acc.account_name.toLowerCase().trim(), acc);
                }
            });

            addLog(`Created maps: ${accountIdMap.size / 2} account IDs, ${accountNameMap.size} account names`);

            for (const [index, csvRecord] of csvRecords.entries()) {
                const accountIdFromCsv = csvRecord.account_id;
                const accountName = csvRecord.account_name || csvRecord.Account || csvRecord['Account Name'];
                const fitScore = parseFloat(csvRecord.account_fit_score || csvRecord['Fit Score'] || csvRecord.fit_score);
                
                const identifier = accountIdFromCsv || accountName;

                if (!identifier) {
                    addLog(`[${index + 1}/${csvRecords.length}] Skipping record - no account_id or account_name found.`, 'warning');
                    tempSummary.errors++;
                    continue;
                }

                if (isNaN(fitScore)) {
                    addLog(`[${index + 1}/${csvRecords.length}] Skipping ${identifier} - invalid fit score: ${csvRecord.account_fit_score || csvRecord['Fit Score'] || csvRecord.fit_score}`, 'warning');
                    tempSummary.errors++;
                    continue;
                }

                addLog(`[${index + 1}/${csvRecords.length}] Processing: ${identifier} (Score: ${fitScore})`);
                
                let accountToUpdate = null;
                
                // Try matching by account_id first (exact and case-insensitive)
                if (accountIdFromCsv) {
                    accountToUpdate = accountIdMap.get(accountIdFromCsv) || accountIdMap.get(accountIdFromCsv.toLowerCase().trim());
                    if (accountToUpdate) {
                        addLog(`Found match by account_id: ${accountIdFromCsv} -> ${accountToUpdate.account_name}`);
                    }
                }
                
                // Fall back to matching by account name
                if (!accountToUpdate && accountName) {
                    accountToUpdate = accountNameMap.get(accountName.toLowerCase().trim());
                    if (accountToUpdate) {
                        addLog(`Found match by account_name: ${accountName} -> ${accountToUpdate.account_name}`);
                    }
                }

                if (accountToUpdate) {
                    try {
                        await AccountFinal.update(accountToUpdate.id, {
                            account_fit_score: fitScore
                        });
                        addLog(`✓ Successfully updated ${accountToUpdate.account_name} with score ${fitScore}.`, 'success');
                        tempSummary.updated++;
                    } catch (error) {
                        addLog(`✗ Error updating ${accountToUpdate.account_name}: ${error.message}`, 'error');
                        tempSummary.errors++;
                    }
                } else {
                    addLog(`⚠ Account not found in database: ${identifier}`, 'warning');
                    // Log available similar account IDs for debugging
                    if (accountIdFromCsv) {
                        const similarIds = Array.from(accountIdMap.keys()).filter(id => id.includes(accountIdFromCsv.substring(0, 10)));
                        if (similarIds.length > 0) {
                            addLog(`   Similar account IDs found: ${similarIds.slice(0, 3).join(', ')}`, 'info');
                        }
                    }
                    tempSummary.notFound++;
                }
                
                setSummary({...tempSummary});
            }
            
            addLog('--- UPDATE COMPLETE ---', 'info');
            setStatus('done');

        } catch (error) {
            addLog(`Fatal error: ${error.message}`, 'error');
            setStatus('idle');
        }
    };

    return (
        <div className="max-w-6xl mx-auto p-8 bg-white shadow-lg rounded-lg my-10">
            <Card className="mb-6">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Upload className="w-6 h-6" />
                        Account Fit Score CSV Upload
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <Tabs defaultValue="upload">
                        <TabsList>
                            <TabsTrigger value="upload">Upload Data</TabsTrigger>
                            <TabsTrigger value="diagnostic">Diagnostic Tool</TabsTrigger>
                        </TabsList>
                        
                        <TabsContent value="upload" className="mt-4">
                            <p className="mb-4 text-gray-600">
                                Paste your CSV or Tab-separated data below. The data must have a header row and columns for the fit score and an account identifier.
                                <br />
                                Supported identifiers: <strong>account_id</strong> (preferred) or account_name/Account/"Account Name".
                                <br />
                                Supported score columns: <strong>account_fit_score</strong>, "Fit Score", or fit_score.
                            </p>
                            
                            <textarea
                                value={csvData}
                                onChange={handleCsvChange}
                                placeholder={"account_id\taccount_fit_score\naustralia_001_009\t79\naustralia_001_040\t75"}
                                className="w-full h-40 p-3 border border-gray-300 rounded-lg font-mono text-sm"
                                disabled={status === 'running'}
                            />
                            
                            <div className="mt-4 flex gap-4">
                                {status === 'idle' && (
                                    <Button onClick={runUpdate} className="bg-blue-600 hover:bg-blue-700">
                                        Process CSV Data
                                    </Button>
                                )}

                                {status === 'running' && (
                                    <div className="flex items-center gap-3 text-lg font-semibold text-gray-700">
                                        <Loader2 className="w-6 h-6 animate-spin" />
                                        Processing... Please do not close this window.
                                    </div>
                                )}
                                
                                {status === 'done' && (
                                    <div className="flex items-center gap-3 text-lg font-semibold text-green-600">
                                        <CheckCircle className="w-6 h-6" />
                                        Process Complete!
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                        
                        <TabsContent value="diagnostic" className="mt-4">
                            <div className="space-y-4">
                                <p className="text-gray-600">
                                    Use this tool to see what account_ids actually exist in your database and troubleshoot matching issues.
                                </p>
                                
                                <Button onClick={runDiagnostic} disabled={isLoadingDiagnostic} className="bg-green-600 hover:bg-green-700">
                                    {isLoadingDiagnostic ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Search className="w-4 h-4 mr-2" />}
                                    Run Diagnostic
                                </Button>
                                
                                {diagnosticData && (
                                    <div className="bg-gray-50 p-4 rounded-lg space-y-4">
                                        <h3 className="font-semibold">Database Diagnostic Results:</h3>
                                        
                                        <div className="grid grid-cols-3 gap-4 text-center">
                                            <div className="bg-white p-3 rounded">
                                                <div className="text-2xl font-bold">{diagnosticData.totalAccounts}</div>
                                                <div className="text-sm text-gray-600">Total Accounts</div>
                                            </div>
                                            <div className="bg-white p-3 rounded">
                                                <div className="text-2xl font-bold text-green-600">{diagnosticData.accountsWithIds}</div>
                                                <div className="text-sm text-gray-600">With account_id</div>
                                            </div>
                                            <div className="bg-white p-3 rounded">
                                                <div className="text-2xl font-bold text-red-600">{diagnosticData.accountsWithoutIds}</div>
                                                <div className="text-sm text-gray-600">Without account_id</div>
                                            </div>
                                        </div>
                                        
                                        {diagnosticData.sampleAccountIds.length > 0 && (
                                            <div>
                                                <h4 className="font-medium mb-2">Sample Account IDs in Database:</h4>
                                                <div className="bg-white p-3 rounded font-mono text-sm max-h-40 overflow-y-auto">
                                                    {diagnosticData.sampleAccountIds.map((acc, i) => (
                                                        <div key={i} className="flex justify-between">
                                                            <span className="text-blue-600">{acc.account_id}</span>
                                                            <span className="text-gray-600">{acc.account_name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {diagnosticData.allAccountIds && (
                                            <div>
                                                <h4 className="font-medium mb-2">All Account IDs ({diagnosticData.allAccountIds.length}):</h4>
                                                <div className="bg-white p-3 rounded font-mono text-xs max-h-60 overflow-y-auto">
                                                    {diagnosticData.allAccountIds.join(', ')}
                                                </div>
                                            </div>
                                        )}
                                        
                                        {diagnosticData.sampleAccountsWithoutIds.length > 0 && (
                                            <div>
                                                <h4 className="font-medium mb-2">Sample Accounts WITHOUT account_id:</h4>
                                                <div className="bg-white p-3 rounded font-mono text-sm max-h-40 overflow-y-auto">
                                                    {diagnosticData.sampleAccountsWithoutIds.map((acc, i) => (
                                                        <div key={i} className="flex justify-between">
                                                            <span className="text-gray-400">No account_id</span>
                                                            <span className="text-gray-600">{acc.account_name}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </TabsContent>
                    </Tabs>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <div className="flex justify-between items-center">
                        <h2 className="font-semibold">Processing Summary</h2>
                        <div className="flex gap-6 text-sm">
                            <div className="text-green-600">Updated: {summary.updated}</div>
                            <div className="text-yellow-600">Not Found: {summary.notFound}</div>
                            <div className="text-red-600">Errors: {summary.errors}</div>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="bg-gray-900 text-white font-mono text-sm rounded-lg p-4 h-64 overflow-y-auto">
                        {logs.length === 0 ? (
                            <div className="text-gray-400">Processing logs will appear here...</div>
                        ) : (
                            logs.map((log, index) => (
                                <div key={index} className={`flex items-start ${
                                    log.type === 'success' ? 'text-green-400' :
                                    log.type === 'error' ? 'text-red-400' :
                                    log.type === 'warning' ? 'text-yellow-400' : ''
                                }`}>
                                    {log.type === 'error' && <AlertTriangle className="w-4 h-4 mr-2 mt-0.5 shrink-0" />}
                                    <span className="break-all">{log.message}</span>
                                </div>
                            ))
                        )}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
